import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, Alert, Typography } from '@mui/material';

import { skillsStore } from 'stores/skillsStore';
import { observer } from 'mobx-react-lite';
import HrAvatar from 'components/Interview/HrAvatar';
import QuestionProgress from 'components/Interview/QuestionProgress';
import { useVideoRecording } from 'hooks/useVideoRecording';
import VideoPreview from 'components/Interview/VideoPreview';
import RecordingControls from 'components/Interview/RecordingControls';
import { VoiceActivityStreamDetector } from 'components/Interview/VoiceActivityStreamDetector';
import { personalDataStore } from 'stores/personalDataStore';

const ExtractSkills = () => {
  // const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id: interviewId } = useParams();
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(true);
  // Реф-гард: useState обновляется асинхронно, поэтому два быстрых клика
  // прошли бы проверку isSubmitting === false. Реф гарантирует один сабмит.
  const submittingRef = useRef(false);
  // Инициализацию/редирект делаем один раз: иначе getExtractInterviews внутри
  // sendAnswer обновляет extractInterview и эффект редиректит прямо посреди прохождения.
  const initedRef = useRef(false);

  const currentIndex = skillsStore.questionIndex;

  const currentQuestion = skillsStore.questions[skillsStore.questionIndex];

  const extractInterview = useMemo(() => {
    return personalDataStore.extractInterviews.find((i) => i.interviewId === interviewId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, personalDataStore.extractInterviews]);

  useEffect(() => {
    if (skillsStore.questionIndex && !currentQuestion) {
      skillsStore.questionIndex = skillsStore.questionIndex - 1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsStore.questionIndex, currentQuestion]);

  // useEffect(() => {
  //   if(!extractInterview?.vacancyId && process.env.REACT_APP_IS_HR_DEMO === "true") {
  //     navigate('/main')
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [extractInterview])

  useEffect(() => {
    return () => {
      skillsStore.questions = [];
      skillsStore.interviewId = null;
      skillsStore.authInterviewId = null;
      skillsStore.questionIndex = 0;
    };
  }, []);

  useEffect(() => {
    if (!extractInterview || initedRef.current) return;
    initedRef.current = true;

    // Интервью уже пройдено — сразу к результатам.
    if (extractInterview.results?.summary || extractInterview.isAnswered) {
      navigate(`/skill-lvl/${extractInterview.interviewId}`);
      return;
    }

    if (!skillsStore.questions.length) {
      skillsStore.questions = extractInterview.questions;
      skillsStore.interviewId = extractInterview.interviewId;
      skillsStore.authInterviewId = extractInterview.id;
      skillsStore.questionIndex = extractInterview.index || 0;
    }
  }, [extractInterview, navigate]);

  const setCurrentIndex = (index: number) => {
    skillsStore.setQuestionIndex(index);
  };

  const handleStopRecording = () => {
    stopRecording();
    setIsPreview(true);
    setIsFinished(true);
  };

  const {
    isRecording,
    recordingTime,
    hasVideoAccess,
    videoRef,
    recordedBlob,
    startRecording,
    stopRecording,
    devicesError,
    clearDevicesError
  } = useVideoRecording({ videoQuality: 'low' });

  useEffect(() => {
    if (error) {
      setError(skillsStore.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillsStore.error]);

  const handleSubmitRecording = async () => {
    if (submittingRef.current || !recordedBlob) return;

    const answeredIndex = currentIndex;
    const total = skillsStore.questions.length;
    const isLast = answeredIndex + 1 >= total;

    submittingRef.current = true;
    setIsSubmitting(true);
    setIsLoading(true);
    setError(null);

    try {
      const ok = await skillsStore.sendAnswer(recordedBlob, answeredIndex, total);
      if (!ok) {
        setError(skillsStore.error || 'Произошла ошибка при загрузке результатов');
        return;
      }

      if (isLast) {
        // Результаты грузит SkillLevelPage (isAnswered уже сохранён выше).
        navigate(`/skill-lvl/${skillsStore.interviewId}`);
      } else {
        setCurrentIndex(answeredIndex + 1);
        setIsFinished(false);
      }
    } catch (error) {
      console.error('Error submitting recording:', error);
      setError('Произошла ошибка при загрузке результатов');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const handleStartClick = useCallback(async () => {
    // Получаем выбранные устройства из localStorage
    try {
      setError(null);
      const selectedMicrophone = localStorage.getItem('selectedMicrophone') || '';
      const selectedCamera = localStorage.getItem('selectedCamera') || '';
      await startRecording(selectedMicrophone, selectedCamera);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : devicesError || 'Произошла ошибка при запуске записи';
      setError(errorMessage);
    }
  }, [startRecording, devicesError]);

  // Управляем isPreview в зависимости от isRecording
  useEffect(() => {
    if (isRecording) {
      setIsPreview(false); // Во время записи убираем превью
    } else if (hasVideoAccess) {
      setIsPreview(true); // Когда есть доступ к видео и нет записи - показываем превью
    }
  }, [isRecording, hasVideoAccess]);

  // Фразы для «думающего» аватара: на последнем вопросе идёт долгий расчёт навыков,
  // на остальных — короткая пауза перед следующим вопросом.
  const thinkingMessages = useMemo(() => {
    return currentIndex + 1 >= skillsStore.questions.length
      ? [
          'Анализирую ваши ответы…',
          'Определяю ваши навыки…',
          'Собираю итоговую картину, это займёт 1–2 минуты…',
          'Почти готово…'
        ]
      : ['Думаю над следующим вопросом…', 'Обрабатываю ваш ответ…', 'Формулирую вопрос…'];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  if (isLoading) {
    return (
      <Stack
        flexDirection="column"
        sx={{ height: '100%', overflow: 'auto', ml: -3, mr: -3, pl: 3, pr: 3 }}
        spacing={3}
      >
        {currentQuestion ? (
          <Box>
            <QuestionProgress
              maxCoount={skillsStore.questions.length}
              questionIndex={currentIndex}
            />
          </Box>
        ) : null}
        <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }} spacing={3}>
          <HrAvatar thinking thinkingMessages={thinkingMessages} text="" />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      flexDirection="column"
      sx={{ height: '100%', overflow: 'auto', ml: -3, mr: -3, pl: 3, pr: 3 }}
      spacing={3}
    >
      {currentQuestion ? (
        <Box>
          <QuestionProgress maxCoount={skillsStore.questions.length} questionIndex={currentIndex} />
        </Box>
      ) : null}
      {(error || devicesError) && (
        <Alert
          severity="error"
          onClose={() => {
            setError(null);
            clearDevicesError();
          }}
        >
          {error || devicesError}
        </Alert>
      )}
      {!error ? (
        <Stack
          justifyContent="space-between"
          sx={{ height: '100%' }}
          alignItems="center"
          spacing={3}
        >
          <Box>
            {currentQuestion ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Вопрос {currentIndex + 1}
                {currentQuestion.title ? ` · ${currentQuestion.title}` : ''}
              </Typography>
            ) : null}
            {/* HrAvatar смонтирован всегда — иначе видео-элемент камеры (videoRef) пропадёт
              и запись/детектор голоса перестанут работать в моменты без текущего вопроса. */}
            <HrAvatar
              text={currentQuestion?.text ?? ''}
            />
          </Box>
          {/* Блок «вы»: вебкамера + индикатор громкости микрофона рядом */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ position: 'relative', pb: 6, width: 260 }}
          >
            <VideoPreview
              ref={videoRef}
              isRecording={isRecording}
              isPreview={isPreview}
              hasVideoAccess={hasVideoAccess}
              corner
            />
            <VoiceActivityStreamDetector
              getStream={() => (videoRef.current?.srcObject as MediaStream | null) ?? null}
              isRecording={isRecording}
            />
          </Stack>
        </Stack>
      ) : null}
      {/* <Box sx={{ mt: 4 }}>{isRecording && <RecordingTimer recordingTime={recordingTime} />}</Box> */}

      <Box sx={{ mt: 'auto !important' }}>
        <RecordingControls
          isRecording={isRecording}
          isFinished={isFinished}
          onStopRecording={handleStopRecording}
          onStartRecording={handleStartClick}
          onSubmitRecording={handleSubmitRecording}
          isSubmitting={isSubmitting}
          recordingTime={recordingTime}
          isStopping={false}
        />
      </Box>
    </Stack>
  );
};

export default observer(ExtractSkills);
